import React, { useMemo, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatIndiaTime } from '../config/india-timezone';
import WhatsAppTemplateForm, { parseTemplateToForm, canEditMetaTemplateStatus } from '../components/WhatsAppTemplateForm';
import MetaTemplateLibraryPanel, { mapLibraryToFormInitial } from '../components/MetaTemplateLibraryPanel';
import TemplateEditorSidebar from '../components/TemplateEditorSidebar';
import LibraryTemplateSidebarForm from '../components/LibraryTemplateSidebarForm';
import WhatsAppTemplatePreview, { previewFromTemplate } from '../components/WhatsAppTemplatePreview';
import {
  FileText,
  Search,
  Download,
  Plus,
  Pencil,
  Trash2,
  Eye,
  Copy,
  BookOpen,
  LayoutList
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'PAUSED', label: 'Paused' }
];

const statusBadgeClass = (status) => {
  if (status === 'APPROVED') return 'bg-green-100 text-green-800';
  if (status === 'PENDING') return 'bg-yellow-100 text-yellow-800';
  if (status === 'REJECTED') return 'bg-red-100 text-red-800';
  if (status === 'PAUSED') return 'bg-orange-100 text-orange-800';
  return 'bg-gray-100 text-gray-800';
};

const Templates = () => {
  const { currentWorkspace } = useWorkspace();

  const [activeTab, setActiveTab] = useState('library');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sidebar, setSidebar] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [highlightKey, setHighlightKey] = useState(null);

  const { data: permissionsData } = useQuery(
    ['myPermissions', currentWorkspace?.id],
    async () => {
      const res = await apiClient.get(
        `/api/workspaces/${currentWorkspace.id}/users/me/permissions`,
        { headers: { 'x-workspace-id': currentWorkspace.id } }
      );
      return res.data;
    },
    { enabled: !!currentWorkspace?.id }
  );

  const permissions = permissionsData?.permissions || [];
  const canView = permissions.includes('templates.view');
  const canCreate = permissions.includes('templates.create');
  const canEdit = permissions.includes('templates.edit');
  const canDelete = permissions.includes('templates.delete');

  const deleteMutation = useMutation(
    async (target) => {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      await apiClient.delete('/api/whatsapp/templates/manage', {
        headers,
        data: { name: target.name, language: target.language }
      });
    },
    {
      onSuccess: () => {
        toast.success('Template deleted from Meta');
        setDeleteTarget(null);
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete template');
      }
    }
  );

  const { data: templatesData, isLoading, isFetching, refetch } = useQuery(
    ['facebookTemplatesManage', currentWorkspace?.id, statusFilter],
    async () => {
      if (!currentWorkspace?.id) return { templates: [] };
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await apiClient.get('/api/whatsapp/templates/manage', { headers, params });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id && activeTab === 'manage',
      staleTime: 60 * 1000
    }
  );

  const filteredTemplates = useMemo(() => {
    const templates = templatesData?.templates || [];
    const needle = searchTerm.toLowerCase();
    return templates.filter((template) => {
      const hayName = (template.name || '').toLowerCase();
      const hayContent = (template.content || '').toLowerCase();
      return hayName.includes(needle) || hayContent.includes(needle);
    });
  }, [templatesData?.templates, searchTerm]);

  const getBodyText = (template) => {
    if (template.components && Array.isArray(template.components)) {
      const body = template.components.find((c) => c.type === 'BODY');
      return body?.text || '';
    }
    return template.content || '';
  };

  const closeSidebar = () => setSidebar(null);

  const handleSubmitSuccess = (result) => {
    const status = result?.status || 'PENDING';
    const name = result?.name;
    const language = result?.language || 'en_US';
    closeSidebar();
    setActiveTab('manage');
    if (status === 'PENDING') setStatusFilter('PENDING');
    else if (status === 'APPROVED') setStatusFilter('APPROVED');
    setHighlightKey(`${name}-${language}`);
    setTimeout(() => refetch(), 300);
    toast.success(
      result?.message ||
        (status === 'PENDING'
          ? `Template submitted — status PENDING (Meta review)`
          : `Template saved — status ${status}`)
    );
    setTimeout(() => setHighlightKey(null), 8000);
  };

  const openPreview = async (template) => {
    const headers = { 'x-workspace-id': currentWorkspace.id };
    const id = template.template_id || template.id;
    try {
      const res = await apiClient.get(`/api/whatsapp/templates/manage/${encodeURIComponent(id)}`, {
        headers,
        params: { name: template.name, language: template.language }
      });
      setSidebar({ type: 'preview', template: res.data.template });
    } catch {
      setSidebar({ type: 'preview', template });
    }
  };

  const openDuplicate = async (template) => {
    const headers = { 'x-workspace-id': currentWorkspace.id };
    const id = template.template_id || template.id;
    let fullTemplate = template;
    try {
      const res = await apiClient.get(`/api/whatsapp/templates/manage/${encodeURIComponent(id)}`, {
        headers,
        params: { name: template.name, language: template.language }
      });
      fullTemplate = res.data.template;
    } catch {
      // use list row if detail fetch fails
    }
    const initial = parseTemplateToForm(fullTemplate);
    const baseName = (template.name || 'template').replace(/_v\d+$/i, '');
    let newName = `${baseName}_v2`;
    if (newName.length > 512) newName = newName.slice(0, 512);
    setSidebar({
      type: 'create',
      formInitial: { ...initial, name: newName }
    });
    toast('Meta cannot edit PENDING templates — create a copy with a new name.', { icon: 'ℹ️' });
  };

  const tabClass = (tab) =>
    `flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
      activeTab === tab
        ? 'border-blue-600 text-blue-700'
        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
    }`;

  const sidebarTitle = () => {
    if (!sidebar) return '';
    if (sidebar.type === 'create') return 'Create WhatsApp template';
    if (sidebar.type === 'edit') return `Edit: ${sidebar.template?.name}`;
    if (sidebar.type === 'library') return 'Add from Meta library';
    if (sidebar.type === 'preview') return sidebar.template?.name || 'Preview';
    return 'Template';
  };

  const sidebarSubtitle = () => {
    if (sidebar?.type === 'library') return sidebar.libraryItem?.name;
    if (sidebar?.type === 'preview') {
      return `${sidebar.template?.status || ''} · ${sidebar.template?.language || ''}`;
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
          <p className="text-sm text-gray-600 mt-1">
            Pick from Meta&apos;s template library or manage your business templates.
          </p>
        </div>
        {activeTab === 'manage' && (
          <div className="flex flex-wrap gap-2">
            {canCreate && (
              <button
                onClick={() => setSidebar({ type: 'create' })}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm"
              >
                <Plus className="h-4 w-4" />
                New template
              </button>
            )}
            <button
              onClick={() => refetch().then(() => toast.success('Refreshed from Meta'))}
              disabled={isFetching}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 flex items-center gap-2 text-sm disabled:opacity-50"
            >
              <Download className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              {isFetching ? 'Refreshing…' : 'Refresh from Meta'}
            </button>
          </div>
        )}
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex gap-1">
          <button type="button" onClick={() => setActiveTab('library')} className={tabClass('library')}>
            <BookOpen className="h-4 w-4" />
            Template Library
          </button>
          <button type="button" onClick={() => setActiveTab('manage')} className={tabClass('manage')}>
            <LayoutList className="h-4 w-4" />
            Manage Templates
          </button>
        </nav>
      </div>

      {activeTab === 'library' && canView && (
        <MetaTemplateLibraryPanel
          workspaceId={currentWorkspace?.id}
          canCreate={canCreate}
          onSelectTemplate={(lib) => setSidebar({ type: 'library', libraryItem: lib })}
        />
      )}

      {activeTab === 'library' && !canView && (
        <p className="text-gray-500 text-sm py-8">You do not have permission to view the template library.</p>
      )}

      {activeTab === 'manage' && (
        <>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search your templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span className="text-xs text-gray-500">
                {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} from Meta
              </span>
            </div>
          </div>

          {isLoading || isFetching ? (
            <div className="text-center py-12 text-gray-500">Loading templates from Meta…</div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">No templates found.</p>
              {canCreate && (
                <button
                  type="button"
                  onClick={() => setActiveTab('library')}
                  className="mt-4 text-sm text-blue-600 hover:text-blue-800"
                >
                  Go to Template Library →
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredTemplates.map((template) => {
                const rowKey = `${template.name}-${template.language}`;
                const isHighlighted = highlightKey === rowKey;
                return (
                  <div
                    key={`${template.id}-${template.language}`}
                    className={`bg-white border rounded-xl p-6 shadow-sm hover:shadow-md transition-all ${
                      isHighlighted
                        ? 'border-blue-500 ring-2 ring-blue-200'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <FileText className="h-5 w-5 text-green-600 shrink-0" />
                          <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${statusBadgeClass(template.status)}`}>
                            {template.status || 'Unknown'}
                          </span>
                          {template.category && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700">
                              {template.category}
                            </span>
                          )}
                          {isHighlighted && (
                            <span className="text-xs text-blue-600 font-medium">Just submitted</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">{getBodyText(template) || '—'}</p>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                          <span>Language: {template.language || 'en_US'}</span>
                          {template.created_time && (
                            <span>Created: {formatIndiaTime(template.created_time, 'relative')}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          onClick={() => openPreview(template)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="Preview"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {canEdit && canEditMetaTemplateStatus(template.status) && (
                          <button
                            type="button"
                            onClick={() => setSidebar({ type: 'edit', template })}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        {canCreate && template.status === 'PENDING' && (
                          <button
                            type="button"
                            onClick={() => openDuplicate(template)}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                            title="Duplicate as new template (Meta blocks editing while PENDING)"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(template)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <TemplateEditorSidebar
        open={!!sidebar}
        title={sidebarTitle()}
        subtitle={sidebarSubtitle()}
        onClose={closeSidebar}
      >
        {sidebar?.type === 'create' && (
          <WhatsAppTemplateForm
            mode="create"
            formInitial={sidebar.formInitial}
            workspaceId={currentWorkspace?.id}
            onSuccess={handleSubmitSuccess}
            onCancel={closeSidebar}
          />
        )}
        {sidebar?.type === 'edit' && (
          <WhatsAppTemplateForm
            mode="edit"
            initialTemplate={sidebar.template}
            workspaceId={currentWorkspace?.id}
            onSuccess={handleSubmitSuccess}
            onCancel={closeSidebar}
          />
        )}
        {sidebar?.type === 'library' && (
          <LibraryTemplateSidebarForm
            libraryItem={sidebar.libraryItem}
            workspaceId={currentWorkspace?.id}
            onCustomize={(lib) =>
              setSidebar({ type: 'create', formInitial: mapLibraryToFormInitial(lib) })
            }
            onSuccess={handleSubmitSuccess}
            onCancel={closeSidebar}
          />
        )}
        {sidebar?.type === 'preview' && (
          <div className="flex flex-1 min-h-0 items-start justify-center p-6 bg-[#f0f2f5] overflow-y-auto">
            <div className="w-full max-w-sm space-y-4">
              <div className="flex flex-wrap gap-2 justify-center">
                <span className={`px-2 py-0.5 text-xs rounded-full ${statusBadgeClass(sidebar.template?.status)}`}>
                  {sidebar.template?.status}
                </span>
                {sidebar.template?.category && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700">
                    {sidebar.template.category}
                  </span>
                )}
              </div>
              <WhatsAppTemplatePreview form={previewFromTemplate(sidebar.template)} sidebar />
            </div>
          </div>
        )}
      </TemplateEditorSidebar>

      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete template?</h2>
            <p className="text-sm text-gray-600 mb-4">
              This removes <strong>{deleteTarget.name}</strong> ({deleteTarget.language}) from Meta.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(deleteTarget)}
                disabled={deleteMutation.isLoading}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isLoading ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Templates;
